import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useGetUserTicketsQuery } from '../store/api/ticketApi';
import { Loader } from '../components/ui/Loader';
import type { Ticket } from '../store/api/ticketApi';

function getStatusColor(status: string) {
  switch (status) {
    case 'open':
      return 'text-green-500 bg-green-500/10';
    case 'pending':
      return 'text-yellow-500 bg-yellow-500/10';
    case 'closed':
      return 'text-gray-500 bg-gray-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
}

function getPriorityColor(priority: string) {
  switch (priority) {
    case 'high':
      return 'text-red-500 bg-red-500/10';
    case 'medium':
      return 'text-yellow-500 bg-yellow-500/10';
    case 'low':
      return 'text-blue-500 bg-blue-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
}

export function TicketsListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sortBy: 'newest',
    page: 1,
    limit: 10,
  });

  const { data, isLoading, error } = useGetUserTicketsQuery(filters);
  const tickets = data?.data || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.network')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/dashboard"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('tickets.list.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('tickets.list.subtitle')}
            </p>
          </div>

          {/* Create Ticket Button */}
          <div className="flex justify-end mb-6 animate-scale-in">
            <Link
              to="/tickets/create"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('tickets.list.createNew')}
            </Link>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6 animate-scale-in">
            <div className="flex-1">
              <input
                type="text"
                placeholder={t('tickets.list.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
            >
              <option value="">{t('tickets.list.allStatuses')}</option>
              <option value="open">{t('tickets.status.open')}</option>
              <option value="pending">{t('tickets.status.pending')}</option>
              <option value="closed">{t('tickets.status.closed')}</option>
            </select>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters({ ...filters, sortBy: e.target.value, page: 1 })}
              className="px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
            >
              <option value="newest">{t('tickets.list.sortNewest')}</option>
              <option value="oldest">{t('tickets.list.sortOldest')}</option>
              <option value="mostActive">{t('tickets.list.sortMostActive')}</option>
            </select>
          </div>

          {/* Tickets List */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
            {tickets.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto mb-4 text-neutral-medium opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4z" />
                </svg>
                <p className="text-neutral-medium text-lg font-body mb-4">
                  {t('tickets.list.empty')}
                </p>
                <p className="text-neutral-medium mb-6">
                  {t('tickets.list.emptyDesc')}
                </p>
                <Link
                  to="/tickets/create"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('tickets.list.createFirst')}
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket: Ticket) => (
                  <Link
                    key={ticket._id}
                    to={`/tickets/${ticket._id}`}
                    className="block p-6 bg-neutral/5 rounded-2xl border border-neutral/10 hover:border-brand-accent/30 hover:shadow-lg hover:shadow-brand-accent/10 transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-lg font-heading font-semibold text-neutral-light truncate min-w-0">
                            {ticket.title}
                          </h3>
                          <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                            {t(`tickets.status.${ticket.status}`)}
                          </span>
                          <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                            {t(`tickets.priority.${ticket.priority}`)}
                          </span>
                        </div>
                        <p className="text-neutral-medium text-sm mb-2">
                          {t('tickets.details.category')}: {t(`tickets.category.${ticket.category}`)}
                        </p>
                        <p className="text-neutral-medium text-sm">
                          {formatDate(ticket.lastMessageAt || ticket.createdAt)}
                        </p>
                      </div>
                      <svg className="w-5 h-5 text-neutral-medium" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Back Button */}
          <div className="text-center">
            <Link
              to="/dashboard"
              className="inline-block px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
            >
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
